import _, {countBy} from 'lodash';

import {Localizer} from '../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage} from '../../../libs/shared/core/src/helpers/internationalization/message';
import {CustomError} from '../../../libs/shared/core/src/helpers/types/customErrors';
import {MessageTypesEnum} from '../../../libs/shared/core/src/models/api/commonMessageModel';
import {LinkedConversationStatusesEnum} from '../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {collectEnumValues} from '../../../libs/shared/core/src/models/api/types/enumTypes';
import {HttpResponseError} from '../../../libs/shared/core/src/services/http/httpError';
import {ConversationBaseModel} from '../../models/api/conversationModel';
import {helpCenterUrl} from '../../store/stateless/navigationActions';

/*
 * Constants.
 */

export const learnMoreErrorUrl = `${helpCenterUrl}/en/articles/478144#when_conversations_cannot_be_linked`;

/*
 * Enums.
 */

export enum ConversationsLinkCheckErrorsEnum {
  TOO_MANY_CONVERSATIONS = 'TOO_MANY_CONVERSATIONS',
  MORE_THAN_ONE_PARENT_OR_CHILD = 'MORE_THAN_ONE_PARENT_OR_CHILD',
  UNSUPPORTED_CHANNEL = 'UNSUPPORTED_CHANNEL',
  NONE = 'NONE',
}

export enum LinkedConversationErrorCodesEnum {
  HAS_LINKED_CONVERSATIONS = 'has_linked_conversations', // Selection includes already linked conversations
  ORIGINAL_IS_RELATED = 'original_is_related', // Selected original conversation is linked already
  LIMIT_EXCEEDED = 'limit_exceeded', // Too many conversations selected – shouldn't happen, but want to display error if it does
}

export class LinkedConversationsError extends CustomError {
  constructor(type: LinkedConversationErrorCodesEnum, message: string) {
    super(message);
    this.name = 'LinkedConversationsError';
  }
}

/*
 * Helpers.
 */

export function canConversationsBeLinked(
  conversations: ReadonlyArray<ConversationBaseModel>,
  hasOmnichannelLinkedConversations: boolean,
): ConversationsLinkCheckErrorsEnum {
  // Limit of 20 conversations selected
  if (conversations.length > 20) {
    return ConversationsLinkCheckErrorsEnum.TOO_MANY_CONVERSATIONS;
  }

  // Can't have more than one parent selected or have a linked convo selected.
  const linkCounts = countBy(conversations, 'linkStatus');
  if (
    linkCounts[LinkedConversationStatusesEnum.ORIGINAL] > 1 ||
    linkCounts[LinkedConversationStatusesEnum.LINKED] > 0
  ) {
    return ConversationsLinkCheckErrorsEnum.MORE_THAN_ONE_PARENT_OR_CHILD;
  }

  // Same message type. - TODO: remove check when we get to cross-channel linking
  const messageTypes = _(conversations)
    .map((c) => c.messageType)
    .uniq()
    .value();

  // Supported message type.
  const messageType = messageTypes[0];
  if (
    !hasOmnichannelLinkedConversations &&
    (messageTypes.length !== 1 || messageType !== MessageTypesEnum.EMAIL)
  ) {
    return ConversationsLinkCheckErrorsEnum.UNSUPPORTED_CHANNEL;
  }

  return ConversationsLinkCheckErrorsEnum.NONE;
}

export function isLinkedConversationErrorCode(error: HttpResponseError) {
  const errorCodes = collectEnumValues(LinkedConversationErrorCodesEnum).map((v) => v.toString());
  return errorCodes.includes(error.code);
}

export function computeLinkedConversationErrorCodeAndMessage(error: HttpResponseError, localize: Localizer) {
  switch (error.code) {
    case LinkedConversationErrorCodesEnum.HAS_LINKED_CONVERSATIONS:
      return {
        code: LinkedConversationErrorCodesEnum.HAS_LINKED_CONVERSATIONS,
        message: localize(
          defineMessage({
            id: 'linked-conversations-error-message-has-linked',
            message:
              'Some selected conversations are already linked to an original conversation. Conversations can only be linked to one original.',
          }),
        ),
      };
    case LinkedConversationErrorCodesEnum.ORIGINAL_IS_RELATED:
      return {
        code: LinkedConversationErrorCodesEnum.ORIGINAL_IS_RELATED,
        message: localize(
          defineMessage({
            id: 'linked-conversations-error-message-original-selected',
            message:
              "Some selected conversations are original conversations. Can't link two original conversations.",
          }),
        ),
      };
    case LinkedConversationErrorCodesEnum.LIMIT_EXCEEDED:
      return {
        code: LinkedConversationErrorCodesEnum.LIMIT_EXCEEDED,
        message: localize(
          defineMessage({
            id: 'linked-conversations-error-message-original-selected',
            message:
              "Max number of conversations selected exceeded. Can't link more than 20 conversations at a time.",
          }),
        ),
      };

    default:
      return {code: null, message: null};
  }
}
